import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";

import { cn } from "@/lib/utils";

/**
 * Drawer Component
 *
 * A drawer component for sliding content from the edges of the screen.
 * Built on top of Vaul drawer primitive.
 *
 * @example
 * ```tsx
 * <Drawer>
 *   <DrawerTrigger>Open Drawer</DrawerTrigger>
 *   <DrawerContent>
 *     <DrawerHeader>
 *       <DrawerTitle>Title</DrawerTitle>
 *       <DrawerDescription>Description</DrawerDescription>
 *     </DrawerHeader>
 *     <DrawerFooter>
 *       <Button>Save</Button>
 *     </DrawerFooter>
 *   </DrawerContent>
 * </Drawer>
 * ```
 */
function Drawer({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Root>) {
    return <DrawerPrimitive.Root data-slot="drawer" {...props} />;
}
Drawer.displayName = "Drawer";

/**
 * DrawerTrigger Component
 *
 * The trigger element that opens the drawer.
 */
function DrawerTrigger({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Trigger>) {
    return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />;
}
DrawerTrigger.displayName = "DrawerTrigger";

/**
 * DrawerPortal Component
 *
 * Portal for rendering drawer content.
 */
function DrawerPortal({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Portal>) {
    return <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />;
}
DrawerPortal.displayName = "DrawerPortal";

/**
 * DrawerClose Component
 *
 * Close button for the drawer.
 */
function DrawerClose({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Close>) {
    return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />;
}
DrawerClose.displayName = "DrawerClose";

/**
 * DrawerOverlay Component
 *
 * Backdrop overlay for the drawer.
 */
function DrawerOverlay({ className, ...props }: React.ComponentProps<typeof DrawerPrimitive.Overlay>) {
    return (
        <DrawerPrimitive.Overlay
            data-slot="drawer-overlay"
            className={cn(
                "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 setu:fixed setu:inset-0 setu:z-50 setu:bg-black/50",
                className
            )}
            {...props}
        />
    );
}
DrawerOverlay.displayName = "DrawerOverlay";

/**
 * DrawerContent Component
 *
 * Main content container for the drawer.
 */
function DrawerContent({ className, children, ...props }: React.ComponentProps<typeof DrawerPrimitive.Content>) {
    return (
        <DrawerPortal data-slot="drawer-portal">
            <DrawerOverlay />
            <DrawerPrimitive.Content
                data-slot="drawer-content"
                className={cn(
                    "group/drawer-content setu:bg-background setu:fixed setu:z-50 setu:flex setu:h-auto setu:flex-col",
                    "setu:data-[vaul-drawer-direction=top]:inset-x-0 setu:data-[vaul-drawer-direction=top]:top-0 setu:data-[vaul-drawer-direction=top]:mb-24 setu:data-[vaul-drawer-direction=top]:max-h-[80vh] setu:data-[vaul-drawer-direction=top]:rounded-b-lg setu:data-[vaul-drawer-direction=top]:!border-b",
                    "setu:data-[vaul-drawer-direction=bottom]:inset-x-0 setu:data-[vaul-drawer-direction=bottom]:bottom-0 setu:data-[vaul-drawer-direction=bottom]:mt-24 setu:data-[vaul-drawer-direction=bottom]:max-h-[80vh] setu:data-[vaul-drawer-direction=bottom]:rounded-t-lg setu:data-[vaul-drawer-direction=bottom]:!border-t",
                    "setu:data-[vaul-drawer-direction=right]:inset-y-0 setu:data-[vaul-drawer-direction=right]:right-0 setu:data-[vaul-drawer-direction=right]:w-3/4 setu:data-[vaul-drawer-direction=right]:!border-l setu:data-[vaul-drawer-direction=right]:sm:max-w-sm",
                    "setu:data-[vaul-drawer-direction=left]:inset-y-0 setu:data-[vaul-drawer-direction=left]:left-0 setu:data-[vaul-drawer-direction=left]:w-3/4 setu:data-[vaul-drawer-direction=left]:!border-r setu:data-[vaul-drawer-direction=left]:sm:max-w-sm",
                    className
                )}
                {...props}
            >
                <div className="setu:bg-muted setu:mx-auto setu:mt-4 setu:hidden setu:h-2 setu:w-[100px] setu:shrink-0 setu:rounded-full setu:group-data-[vaul-drawer-direction=bottom]/drawer-content:block" />
                {children}
            </DrawerPrimitive.Content>
        </DrawerPortal>
    );
}
DrawerContent.displayName = "DrawerContent";

/**
 * DrawerHeader Component
 *
 * Header section of the drawer.
 */
function DrawerHeader({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="drawer-header"
            className={cn(
                "setu:flex setu:flex-col setu:gap-0.5 setu:p-4 setu:group-data-[vaul-drawer-direction=bottom]/drawer-content:text-center setu:group-data-[vaul-drawer-direction=top]/drawer-content:text-center setu:md:gap-1.5 setu:md:text-left",
                className
            )}
            {...props}
        />
    );
}
DrawerHeader.displayName = "DrawerHeader";

/**
 * DrawerFooter Component
 *
 * Footer section of the drawer.
 */
function DrawerFooter({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="drawer-footer"
            className={cn("setu:mt-auto setu:flex setu:flex-col setu:gap-2 setu:p-4", className)}
            {...props}
        />
    );
}
DrawerFooter.displayName = "DrawerFooter";

/**
 * DrawerTitle Component
 *
 * Title of the drawer.
 */
function DrawerTitle({ className, ...props }: React.ComponentProps<typeof DrawerPrimitive.Title>) {
    return (
        <DrawerPrimitive.Title
            data-slot="drawer-title"
            className={cn("setu:text-foreground setu:font-semibold", className)}
            {...props}
        />
    );
}
DrawerTitle.displayName = "DrawerTitle";

/**
 * DrawerDescription Component
 *
 * Description text for the drawer.
 */
function DrawerDescription({ className, ...props }: React.ComponentProps<typeof DrawerPrimitive.Description>) {
    return (
        <DrawerPrimitive.Description
            data-slot="drawer-description"
            className={cn("setu:text-muted-foreground setu:text-sm", className)}
            {...props}
        />
    );
}
DrawerDescription.displayName = "DrawerDescription";

export {
    Drawer,
    DrawerPortal,
    DrawerOverlay,
    DrawerTrigger,
    DrawerClose,
    DrawerContent,
    DrawerHeader,
    DrawerFooter,
    DrawerTitle,
    DrawerDescription,
};
